<template>
  <div>
    <b-table
      ref="table"
      small
      bordered
      striped
      hover
      :fields="fields"
      :items="users.data"
      :per-page="users.pageSize"
      :current-page="$route.path.includes('/group/users') ? page : ''"
      :page="page"
      class="px-0"
      :busy="isLoading"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>

      <!--  Allows for inline edits  -->
      <template v-slot:cell()="{ value, item, index, field: { key }}">
        <template v-if="showEdit(index)">{{ value }}</template>
        <b-form-input v-else v-model="item[key]" class="py-0 px-1 form-control-sm"/>
      </template>

      <!--  Link to view clients  -->
      <template #cell(clients)="data">
        <button class="text-primary" @click="redirectToClients(data)">View Clients</button>
      </template>

      <!--  Link to view group users  -->
      <template #cell(users)="data">
        <button class="text-primary" @click="redirectToGroupUsers(data)">View Users</button>
      </template>

      <!--  File: Owner  -->
      <template #cell(owner)="data">
        <p>{{ data.item.owner.name }}</p>
      </template>

      <!--  File: Shared With  -->
      <template #cell(sharedWith)="data">
        <ul>
          <ul>
            <li class="spnod-admin__form--list" v-for="shared in data.value">
              {{ genSharedWith(shared) }}
            </li>
          </ul>
        </ul>
      </template>

      <!--  Cancel sub on behalf of another User  -->
      <template #cell(cancel)="data">
        <div class="d-flex justify-content-center">
          <button v-if="data.value" class="cancel-button" @click="$emit('handleCancel', data)">Cancel</button>
        </div>
      </template>

      <!--  List of parents  -->
      <template #cell(parents)="data">
        <ul>
          <li class="spnod-admin__form--list" v-for="parent in data.value">{{ parent.name }}</li>
        </ul>
      </template>

      <!--  Action icons  -->
      <template #cell(actions)="data">
        <div class="d-flex justify-content-center align-items-center">
          <div class="d-flex justify-content-center">
            <!--  Link to user account  -->
            <button v-if="hasAction('accountMgmt')" @click="redirectToUserAccount(data)" class="mx-1">
              <b-icon-person-fill title="User Account"/>
            </button>

            <!--  Inline Edit  -->
            <button v-if="hasAction('inlineEdit')" @click="toggleInlineEdit(data)" class="mx-1">
              <b-icon-pencil-square v-if="showEdit(data.index)" title="Edit"/>
              <b-icon-check-circle v-else title="Update" @click="$emit('handleUpdate', data)"/>
            </button>

            <!--  Inline Delete  -->
            <button v-if="hasAction('delete')" @click="$emit('handleDelete', data)" class="mx-1">
              <b-icon-trash-fill title="Delete"/>
            </button>

            <!--  Group Edit  -->
            <button v-if="hasAction('groupEdit')" @click="handleGroupEdit(data)" class="mx-1">
              <b-icon-pencil-square title="Edit"/>
            </button>

            <!--  File Download  -->
            <a v-if="hasAction('download')" :href="data.item.download" target="_blank" class="mx-1 text-dark">
              <b-icon-cloud-download title="Download"/>
            </a>

            <!--  File Edit  -->
            <button v-if="hasAction('fileEdit') && (getMyInfo.hasRoles || data.item.owner._id === getMyInfo._id)" @click="handleFileEdit(data)" class="mx-1">
              <b-icon-pencil-square title="Edit"/>
            </button>

            <!--  Subscription Edit  -->
            <button v-if="hasAction('subscriptionEdit')" @click="handleSubscriptionEdit(data)" class="mx-1">
              <b-icon-pencil-square title="Edit"/>
            </button>
          </div>
        </div>
      </template>
    </b-table>
    <b-pagination
      v-if="users.total > users.pageSize"
      :total-rows="users.total"
      :per-page="users.pageSize"
      :page="page"
      @change="handleChange"
      aria-controls="my-table"
      size="sm"
      class="mb-2"
    ></b-pagination>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AdminTable',
  props: {
    users: Object,
    headers: Array,
    options: Object
  },
  data() {
    return {
      edits: [],
      isLoading: false,
      page: 1
    }
  },
  computed: {
    ...mapGetters(['getMyInfo']),
    fields() {
      let result = this.headers.map(el => ({key: el}))
      if (this.options) {
        // Append columns based on options passed in
        const {sortable, actions} = this.options
        if (sortable) {
          result.forEach(el => {
            el.sortable = true
          })
        }
        if (actions) result.push({key: 'actions'})
      }
      return result
    }
  },
  methods: {
    ...mapActions(['setDietitian', 'setClientId', 'setActiveGroup', 'getUsers', 'getMyUsers', 'getGroups', 'getNutritionists', 'getClients', 'getFiles', 'setActiveFile', 'setSubscriptionToEdit']),
    redirectToUserAccount: async function (e) {
      await this.setClientId(e.item._id)
      this.$router.push('/user/account')
    },
    toggleInlineEdit(e) {
      if (!this.edits.includes(e.index)) {
        this.edits.push(e.index)
      } else {
        const indexOf = this.edits.indexOf((e.index))
        this.edits.splice(indexOf, 1)
      }
    },
    redirectToClients: async function (e) {
      await this.setDietitian(e.item._id)
      this.$router.push('/dietitian/clients')
    },
    redirectToGroupUsers: async function (data) {
      await this.setActiveGroup(data.item._id)
      this.$router.push('/group/users')
    },
    hasAction: function (name) {
      return this.options.actions.includes(name)
    },
    showEdit(index) {
      return !this.edits.includes(index)
    },
    handleGroupEdit: async function (data) {
      await this.setActiveGroup(data.item._id)
      this.$router.push('/group/edit')
    },
    handleFileEdit: async function (data) {
      await this.setActiveFile(data.item._id)
      this.$router.push('/file/edit')
    },
    handleSubscriptionEdit: async function (data) {
      await this.setSubscriptionToEdit(data.item)
      this.$router.push('/subscriptions/edit')
    },
    genSharedWith: function (data) {
      let result = ''
      if (data.group && data.group.name) result = data.group.name
      if (data.client && data.client.name) result = data.client.name
      return result
    },
    handleChange: async function (page) {
      this.page = page
      this.isLoading = true
      if (this.$route.path.includes('/user')) {
        if (this.getMyInfo.hasRoles) {
          await this.getUsers({ page })
        } else {
          await this.getMyUsers({ page })
        }
      } if (this.$route.path.includes('/group') && !this.$route.path.includes('/users')) {
        await this.getGroups({ page })
      } if (this.$route.path.includes('/dietitian')) {
        if (this.$route.path.includes('/clients')) {
          await this.getClients({ page })
        } else {
          await this.getNutritionists({ page })
        }
      } if (this.$route.path.includes('/file')) {
        await this.getFiles({ page })
      }
      this.isLoading = false
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/app/components/AdminTable.vue?4933dc93